import { useEffect, useMemo, useRef, useState } from "react";
import { ParentSize } from "@visx/responsive";
import { Mercator } from "@visx/geo";
import { scaleSequential } from "d3-scale";
import { interpolateRgb } from "d3-interpolate";
import { feature } from "topojson-client";
import type { Feature, FeatureCollection, Geometry } from "geojson";
import { useTooltip, useTooltipInPortal, defaultStyles } from "@visx/tooltip";
import { ChartCard, Skeleton } from "@/components/reusable";
import { fetchFleetByFlag } from "@/services/analyticsApi";
import { useAnalyticsFilters } from "@/hooks/useAnalyticsFilters";
import type { FlagStateDatum } from "@/types/analytics";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore -- JSON topology imported by Vite at build time
import worldTopo from "world-atlas/countries-110m.json";

interface CountryFeatureProps {
  name?: string;
}

type CountryFeature = Feature<Geometry, CountryFeatureProps>;
type CountryFeatureCollection = FeatureCollection<Geometry, CountryFeatureProps>;

function extractCountries(): CountryFeatureCollection {
  // The world-atlas topojson has an `objects.countries` collection; feature() converts to GeoJSON.
  const topo = worldTopo as unknown as Parameters<typeof feature>[0];
  const objects = (topo as { objects: Record<string, unknown> }).objects;
  return feature(topo, objects.countries as never) as unknown as CountryFeatureCollection;
}

const tooltipStyles = {
  ...defaultStyles,
  background: "white",
  border: "1px solid rgba(0,0,0,0.14)",
  color: "var(--color-text-primary)",
  fontFamily: "var(--font-sans)",
  fontSize: 12,
  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  borderRadius: 8,
  padding: "8px 12px",
};

export function FleetByFlagMap() {
  const filters = useAnalyticsFilters();
  const [data, setData] = useState<FlagStateDatum[] | null>(null);

  useEffect(() => {
    let active = true;
    setData(null);
    fetchFleetByFlag(filters).then((d) => {
      if (active) setData(d);
    });
    return () => {
      active = false;
    };
  }, [filters]);

  const byIsoNum = useMemo<Map<string, FlagStateDatum>>(() => {
    const m = new Map<string, FlagStateDatum>();
    if (data) for (const d of data) m.set(d.isoNum.replace(/^0+/, "") || "0", d);
    return m;
  }, [data]);

  const max = data?.reduce((a, d) => Math.max(a, d.vesselCount), 0) ?? 0;

  return (
    <ChartCard
      title="Fleet by flag state"
      subtitle="Vessel count by registration country"
      minHeight={340}
    >
      {!data ? (
        <Skeleton height="h-full" className="min-h-[220px]" />
      ) : (
        <div className="flex flex-col gap-3 h-full">
          <div className="flex-1 min-h-0 w-full">
            <ParentSize>
              {({ width, height }) =>
                width > 0 && height > 0 ? (
                  <MapChart width={width} height={height} byIsoNum={byIsoNum} max={max} />
                ) : null
              }
            </ParentSize>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-[var(--color-text-tertiary)]">
            <span>0</span>
            <div className="flex h-1.5 rounded-full overflow-hidden flex-1">
              {Array.from({ length: 20 }).map((_, i) => (
                <span
                  key={i}
                  className="flex-1"
                  style={{
                    background: interpolateRgb("#E6F1FB", "#185FA5")(i / 19),
                  }}
                />
              ))}
            </div>
            <span>{max || 0}</span>
          </div>
        </div>
      )}
    </ChartCard>
  );
}

interface MapChartProps {
  width: number;
  height: number;
  byIsoNum: Map<string, FlagStateDatum>;
  max: number;
}

function MapChart({ width, height, byIsoNum, max }: MapChartProps) {
  const countries = useMemo(() => extractCountries(), []);
  const colorScale = useMemo(
    () => scaleSequential([0, Math.max(1, max)], interpolateRgb("#E6F1FB", "#185FA5")),
    [max]
  );
  const NO_DATA = "#F1EFE8";
  const STROKE = "rgba(0,0,0,0.08)";

  const { tooltipData, tooltipLeft, tooltipTop, tooltipOpen, showTooltip, hideTooltip } =
    useTooltip<FlagStateDatum>();
  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    scroll: true,
    detectBounds: true,
  });

  // Mercator projection — the sample used natural earth; Mercator is what visx's Mercator component
  // provides out of the box and reads fine for a flag-state heat map at this scale.
  const scale = (width / 6.3) * 1;
  const translate: [number, number] = [width / 2, height / 1.55];

  return (
    <div ref={containerRef} className="relative w-full h-full">
      <svg width={width} height={height}>
        <Mercator<CountryFeature>
          data={countries.features as CountryFeature[]}
          scale={scale}
          translate={translate}
        >
          {(mercator) => (
            <ScalingGroup
              centerX={width / 2}
              centerY={height / 2}
              fromScale={0.92}
              toScale={1}
              durationMs={900}
            >
              {mercator.features.map(({ feature: f, path }, i) => {
                // Topojson country ids are numeric strings (with leading zeros).
                const id =
                  String((f as unknown as { id?: string | number }).id ?? "").replace(/^0+/, "") ||
                  "0";
                const datum = byIsoNum.get(id);
                const fill = datum ? colorScale(datum.vesselCount) : NO_DATA;
                return (
                  <path
                    key={i}
                    d={path ?? ""}
                    fill={String(fill)}
                    stroke={STROKE}
                    strokeWidth={0.4}
                    style={{ transition: "fill 0.6s cubic-bezier(0.22, 1, 0.36, 1)" }}
                    onMouseMove={(e) => {
                      if (!datum) return;
                      const pt = (
                        e.currentTarget.ownerSVGElement as SVGSVGElement | null
                      )?.getBoundingClientRect();
                      showTooltip({
                        tooltipData: datum,
                        tooltipLeft: pt ? e.clientX - pt.left : 0,
                        tooltipTop: pt ? e.clientY - pt.top : 0,
                      });
                    }}
                    onMouseLeave={hideTooltip}
                  />
                );
              })}
            </ScalingGroup>
          )}
        </Mercator>
      </svg>
      {tooltipOpen && tooltipData && (
        <TooltipInPortal top={tooltipTop} left={tooltipLeft} style={tooltipStyles}>
          <div className="font-medium">{tooltipData.name}</div>
          <div className="text-[var(--color-text-secondary)]">
            {tooltipData.vesselCount} vessel{tooltipData.vesselCount === 1 ? "" : "s"} registered
          </div>
        </TooltipInPortal>
      )}
    </div>
  );
}

interface ScalingGroupProps {
  /** X coordinate (in SVG viewport units) to scale about. */
  centerX: number;
  /** Y coordinate (in SVG viewport units) to scale about. */
  centerY: number;
  fromScale: number;
  toScale: number;
  durationMs?: number;
  children: React.ReactNode;
}

/**
 * SVG group that animates its `transform` attribute via ref + RAF on mount.
 * Direct attribute manipulation rather than framer-motion because
 * `transformBox: fill-box` / `transformOrigin` on SVG `<g>` through framer's
 * CSS transform path has unreliable behaviour across browsers — particularly
 * when the `<g>` has many nested path children.
 */
function ScalingGroup({
  centerX,
  centerY,
  fromScale,
  toScale,
  durationMs = 900,
  children,
}: ScalingGroupProps) {
  const ref = useRef<SVGGElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const buildTransform = (s: number) =>
      `translate(${centerX} ${centerY}) scale(${s}) translate(${-centerX} ${-centerY})`;

    if (reduce) {
      el.setAttribute("transform", buildTransform(toScale));
      return;
    }

    let startTime: number | null = null;
    let rafId = 0;
    const ease = (t: number) => 1 - Math.pow(1 - t, 3);

    const step = (now: number) => {
      if (startTime === null) startTime = now;
      const t = Math.min(1, (now - startTime) / durationMs);
      const eased = ease(t);
      const s = fromScale + (toScale - fromScale) * eased;
      el.setAttribute("transform", buildTransform(s));
      if (t < 1) rafId = requestAnimationFrame(step);
    };

    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, [centerX, centerY, fromScale, toScale, durationMs]);

  return (
    <g
      ref={ref}
      transform={`translate(${centerX} ${centerY}) scale(${fromScale}) translate(${-centerX} ${-centerY})`}
    >
      {children}
    </g>
  );
}
